/**
 * Graph adapters: WorkflowGraph -> React Flow nodes/edges (Phase 2, T2.4).
 *
 * Ported from Agent Studio's components/graph/adapt.ts. Feeds BOTH the Build canvas
 * and the Run viewer; `opts.nodeStatus` paints nodes live during a run. A
 * `feedback:true` edge renders dashed (a refine loop), matching the runtime's
 * refine-edge semantics.
 */

import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import type { RunStatus, WorkflowGraph } from '../../types/agentBuilder';
import type { AgentFlowNodeData } from './AgentFlowNode';

interface PaintOpts {
  nodeStatus?: Record<string, RunStatus>;
  confidence?: Record<string, number>;
  guardrailFail?: Record<string, boolean>;
  selectedId?: string | null;
}

export function toFlowNodes(graph: WorkflowGraph, opts: PaintOpts = {}): Node<AgentFlowNodeData>[] {
  return (graph.nodes || []).map((n) => ({
    id: n.id,
    type: 'agentNode',
    position: { x: n.x ?? 0, y: n.y ?? 0 },
    data: {
      node: n,
      status: opts.nodeStatus?.[n.id],
      confidence: opts.confidence?.[n.id],
      guardrailFail: opts.guardrailFail?.[n.id],
      selected: opts.selectedId === n.id,
    },
  }));
}

export function toFlowEdges(graph: WorkflowGraph, opts: PaintOpts = {}): Edge[] {
  return (graph.edges || []).map((e) => {
    const active =
      opts.nodeStatus?.[e.target] === 'running' || opts.nodeStatus?.[e.source] === 'running';
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label ?? undefined,
      // Clean, enterprise orthogonal connectors that meet the Left/Right handles flush
      // (removes the default-bezier "gap" between the arrow and the node).
      type: 'smoothstep',
      animated: active || e.feedback === true,
      style: e.feedback
        ? { stroke: '#10b981', strokeWidth: 1.5, strokeDasharray: '5 4' }
        : active
          ? { stroke: '#6366f1', strokeWidth: 2 }
          : { stroke: '#cbd5e1', strokeWidth: 1.5 },
      labelStyle: { fill: '#64748b', fontSize: 10 },
      labelBgStyle: { fill: '#ffffff', fillOpacity: 0.85 },
      markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: e.feedback ? '#10b981' : '#94a3b8' },
    };
  });
}

/** Read the authoring WorkflowGraph out of an AgentWorkflow's graphJson, filling in
 *  id/name/strategy from the workflow record if the graph is partial. */
export function graphOf(wf: {
  workflowId: string;
  name: string;
  strategy: WorkflowGraph['strategy'];
  graphJson: Record<string, unknown> | WorkflowGraph;
}): WorkflowGraph {
  const gj = (wf.graphJson || {}) as Partial<WorkflowGraph>;
  return {
    id: gj.id || wf.workflowId,
    name: gj.name || wf.name,
    product: gj.product || '',
    description: gj.description || '',
    strategy: gj.strategy || wf.strategy,
    nodes: gj.nodes || [],
    edges: gj.edges || [],
  };
}
